import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Lock, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { http, formatApiError } from "@/lib/api";
import { AMOUNTS, INDUSTRY_OPTIONS, CREDIT, DEBT, BANK, REVENUE, PRODUCTS } from "@/data/content";

const base =
  "w-full rounded-xl border border-[var(--line)] bg-white px-4 py-3 text-[15px] text-[var(--ink)] outline-none transition-[border-color,box-shadow] duration-200 focus:border-[var(--blue)] focus:shadow-[0_0_0_4px_rgba(11,94,215,0.12)]";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-[var(--ink)]/70">{label}</span>
    {children}
  </label>
);

const Select = ({ label, name, value, onChange, options, testid }) => (
  <Field label={label}>
    <select name={name} value={value} onChange={onChange} required data-testid={testid} className={`${base} appearance-none bg-[url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='8'%3E%3Cpath d='M1 1l5 5 5-5' stroke='%23061a3d' stroke-width='1.6' fill='none'/%3E%3C/svg%3E")] bg-[right_1rem_center] bg-no-repeat pr-10`}>
      <option value="" disabled>Select</option>
      {options.map((o) => (
        <option key={o} value={o}>{o}</option>
      ))}
    </select>
  </Field>
);

const EMPTY = {
  desired_amount: "", industry: "", business_start_date: "", credit_score: "", business_debt: "", bank_account_type: "",
  business_name: "", full_name: "", email: "", phone: "", monthly_revenue: "", product: "Merchant Cash Advance", message: "",
};

export const EligibilityForm = () => {
  const [step, setStep] = useState(1);
  const [f, setF] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const onPick = (e) => setF((p) => ({ ...p, product: e.detail }));
    window.addEventListener("a613:product", onPick);
    return () => window.removeEventListener("a613:product", onPick);
  }, []);

  const on = (e) => setF((p) => ({ ...p, [e.target.name]: e.target.value }));

  const next = (e) => {
    e.preventDefault();
    setStep(2);
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await http.post("/applications", f);
      setStep(3);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="eligibility-form" className="relative">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-[var(--green)]">Step {Math.min(step, 2)} of 2</div>
          <h2 className="mt-1 font-display text-xl font-extrabold italic tracking-tight sm:text-2xl">
            {step === 1 ? "Check your eligibility" : step === 2 ? "Where should we send offers?" : "You're in."}
          </h2>
        </div>
        <div className="flex gap-1.5">
          {[1, 2].map((s) => (
            <span key={s} className={`h-1.5 w-8 rounded-full transition-colors duration-500 ${step >= s ? "bg-gradient-to-r from-[var(--blue)] to-[var(--green)]" : "bg-[var(--ink)]/10"}`} />
          ))}
        </div>
      </div>

      {step === 1 && (
        <motion.form key="s1" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} onSubmit={next} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="eligibility-step-1">
          <Select label="Desired amount" name="desired_amount" value={f.desired_amount} onChange={on} options={AMOUNTS} testid="field-desired-amount" />
          <Select label="What does the business do?" name="industry" value={f.industry} onChange={on} options={INDUSTRY_OPTIONS} testid="field-industry" />
          <Field label="Business start date">
            <input type="month" name="business_start_date" value={f.business_start_date} onChange={on} required className={base} data-testid="field-start-date" />
          </Field>
          <Select label="Credit score" name="credit_score" value={f.credit_score} onChange={on} options={CREDIT} testid="field-credit-score" />
          <Select label="Business debt" name="business_debt" value={f.business_debt} onChange={on} options={DEBT} testid="field-business-debt" />
          <Select label="Bank account type" name="bank_account_type" value={f.bank_account_type} onChange={on} options={BANK} testid="field-bank-type" />
          <button type="submit" data-testid="check-eligibility-btn" className="group mt-2 flex items-center justify-center gap-2 rounded-full bg-[var(--blue)] px-6 py-4 font-semibold text-white transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:bg-[var(--ink)] hover:shadow-[0_18px_40px_-12px_rgba(11,94,215,0.6)] sm:col-span-2">
            Check eligibility <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
        </motion.form>
      )}

      {step === 2 && (
        <motion.form key="s2" initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} onSubmit={submit} className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-testid="eligibility-step-2">
          <Field label="Business name">
            <input name="business_name" value={f.business_name} onChange={on} required className={base} data-testid="field-business-name" />
          </Field>
          <Field label="Your full name">
            <input name="full_name" value={f.full_name} onChange={on} required className={base} data-testid="field-full-name" />
          </Field>
          <Field label="Email">
            <input type="email" name="email" value={f.email} onChange={on} required className={base} data-testid="field-email" />
          </Field>
          <Field label="Phone">
            <input type="tel" name="phone" value={f.phone} onChange={on} required minLength={7} className={base} data-testid="field-phone" />
          </Field>
          <Select label="Monthly revenue" name="monthly_revenue" value={f.monthly_revenue} onChange={on} options={REVENUE} testid="field-monthly-revenue" />
          <Select label="Product of interest" name="product" value={f.product} onChange={on} options={PRODUCTS.map((p) => p.name)} testid="field-product" />
          <div className="mt-2 flex gap-3 sm:col-span-2">
            <button type="button" onClick={() => setStep(1)} data-testid="eligibility-back-btn" className="flex items-center justify-center rounded-full border border-[var(--line)] px-5 transition-colors hover:bg-[var(--ink)]/5" aria-label="Back">
              <ArrowLeft className="h-4 w-4" />
            </button>
            <button type="submit" disabled={loading} data-testid="submit-application-btn" className="group flex flex-1 items-center justify-center gap-2 rounded-full bg-[var(--green)] px-6 py-4 font-semibold text-white transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-[var(--ink)] disabled:opacity-70">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>See my options <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>}
            </button>
          </div>
        </motion.form>
      )}

      {step === 3 && (
        <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="py-6 text-center" data-testid="eligibility-success">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-[var(--blue)] to-[var(--green)] text-white">
            <Check className="h-8 w-8" />
          </div>
          <p className="mx-auto mt-5 max-w-sm text-[15px] leading-relaxed text-[var(--ink)]/75">
            Thanks, {f.full_name.split(" ")[0] || "there"}. Your application is in. A 613 Advance specialist will reach out shortly with your options.
          </p>
          <button onClick={() => { setF(EMPTY); setStep(1); }} data-testid="eligibility-restart-btn" className="mt-6 text-sm font-semibold text-[var(--blue)] underline-offset-4 hover:underline">
            Submit another application
          </button>
        </motion.div>
      )}

      {step !== 3 && (
        <p className="mt-5 flex items-center gap-2 text-xs text-[var(--ink)]/60">
          <Lock className="h-3.5 w-3.5" /> Checking eligibility won't affect your credit score.
        </p>
      )}
    </div>
  );
};
